'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { HealthResponse } from '@/lib/types';
import { aiModelsApi, HealthResponse as AiHealthResponse } from '@/lib/ai-models-api';
import PrivateModeToggle from '@/components/PrivateModeToggle';
import ScratchpadPanel from '@/components/ScratchpadPanel';
import { usePrivateMode } from '@/lib/privateMode';

interface MinimalStats {
  processing: number;
  pending: number;
  completed: number;
}

interface StatusBarProps {
  health: HealthResponse | null;
  stats: MinimalStats | null;
  onRefresh: () => void;
}

// `private: true` entries only appear while private mode is unlocked.
// Items marked `group: 'tools'` are collapsed into one dropdown, in their place in the bar.
const navItems: { href: string; label: string; icon: ReactNode; private?: boolean; group?: 'tools' }[] = [
  { href: '/', label: 'Create', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  )},
  { href: '/variants', label: 'Variants', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  )},
  { href: '/collage', label: 'Collage', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  )},
  { href: '/search', label: 'Search', private: true, icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  )},
  { href: '/xplore', label: 'Xplore', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
    </svg>
  )},
  { href: '/pipeline', label: 'Pipeline', group: 'tools', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.39 48.39 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z" />
    </svg>
  )},
  { href: '/pipeline/batch', label: 'Batch', group: 'tools', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 5.25h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5" />
    </svg>
  )},
  { href: '/edit', label: 'Edit', group: 'tools', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
  )},
  { href: '/img2img', label: 'Img2Img', group: 'tools', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
    </svg>
  )},
  { href: '/videos', label: 'Videos', group: 'tools', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 010 1.972l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z" />
    </svg>
  )},
  { href: '/ltx', label: 'LTX', group: 'tools', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-2.625 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-1.5A1.125 1.125 0 0118 18.375M20.625 4.5H3.375m17.25 0c.621 0 1.125.504 1.125 1.125M20.625 4.5h-1.5C18.504 4.5 18 5.004 18 5.625m3.75 0v1.5c0 .621-.504 1.125-1.125 1.125M3.375 4.5c-.621 0-1.125.504-1.125 1.125M3.375 4.5h1.5C5.496 4.5 6 5.004 6 5.625m-3.75 0v1.5c0 .621.504 1.125 1.125 1.125m0 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75C5.496 8.25 6 7.746 6 7.125v-1.5M4.875 8.25C5.496 8.25 6 8.754 6 9.375v1.5m0-5.25v5.25m0-5.25C6 5.004 6.504 4.5 7.125 4.5h9.75c.621 0 1.125.504 1.125 1.125m1.125 2.625h1.5m-1.5 0A1.125 1.125 0 0118 7.125v-1.5m1.125 2.625c-.621 0-1.125.504-1.125 1.125v1.5m2.625-2.625c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125M18 5.625v5.25M7.125 12h9.75m-9.75 0A1.125 1.125 0 016 10.875M7.125 12C6.504 12 6 12.504 6 13.125m0-2.25C6 11.496 5.496 12 4.875 12M18 10.875c0 .621-.504 1.125-1.125 1.125M18 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m-12 5.25v-5.25m0 5.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125m-12 0v-1.5c0-.621-.504-1.125-1.125-1.125M18 18.375v-5.25m0 5.25v-1.5c0-.621.504-1.125 1.125-1.125M18 13.125v1.5c0 .621.504 1.125 1.125 1.125M18 13.125c0-.621.504-1.125 1.125-1.125M6 13.125v1.5c0 .621-.504 1.125-1.125 1.125M6 13.125C6 12.504 5.496 12 4.875 12m-1.5 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75C5.496 12 6 12.504 6 13.125M19.125 12h1.5m0 0c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m0-3.75h-1.5" />
    </svg>
  )},
  { href: '/pose', label: 'Pose', group: 'tools', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  )},
  { href: '/ai-models', label: 'AI Models', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
  )},
  { href: '/favorites', label: 'Favorites', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
    </svg>
  )},
];

/** The grouped nav items behind one button. Shows the open tool's own label when you are
 *  in one, so the bar still says where you are. */
function ToolsMenu({ items, pathname }: { items: typeof navItems; pathname: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = items.find((item) => item.href === pathname);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Navigating does not unmount the bar, so the panel has to be closed by hand
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium cursor-pointer ${
          active
            ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
            : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
        }`}
        title={active ? `Tools — currently in ${active.label}` : 'Tools'}
      >
        {active ? active.icon : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
          </svg>
        )}
        {active ? active.label : 'Tools'}
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-1 min-w-[11rem] py-1 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] shadow-xl z-50"
        >
          {items.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              role="menuitem"
              onClick={() => setOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                item.href === pathname
                  ? 'text-[var(--accent)] bg-[var(--accent-soft)]'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StatusBar({ health, stats, onRefresh }: StatusBarProps) {
  const pathname = usePathname();
  const privateEnabled = usePrivateMode((s) => s.enabled);
  const [aiHealth, setAiHealth] = useState<AiHealthResponse | null>(null);
  const [aiHealthLoading, setAiHealthLoading] = useState(true);
  const [showAiPopup, setShowAiPopup] = useState(false);

  const fetchAiHealth = useCallback(async () => {
    try {
      const res = await aiModelsApi.getHealth();
      setAiHealth(res);
    } catch {
      setAiHealth(null);
    } finally {
      setAiHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAiHealth();
    const interval = setInterval(fetchAiHealth, 10000);
    return () => clearInterval(interval);
  }, [fetchAiHealth]);

  const aiConnected = aiHealth !== null;

  const visibleNav = navItems.filter((item) => !item.private || privateEnabled);
  const toolItems = visibleNav.filter((item) => item.group === 'tools');
  const firstToolIndex = visibleNav.findIndex((item) => item.group === 'tools');

  return (
    <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
      <div className="max-w-7xl mx-auto px-6 h-12 flex items-center justify-between">
        {/* Left: Navigation */}
        <nav className="flex items-center gap-1">
          {visibleNav.map((item, index) => {
            // The grouped run becomes one dropdown, emitted where the run starts so the
            // bar keeps its order.
            if (item.group === 'tools') {
              return index === firstToolIndex
                ? <ToolsMenu key="tools" items={toolItems} pathname={pathname} />
                : null;
            }
            const isActive = pathname === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium ${
                  isActive
                    ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            );
          })}
          {/* Renders itself away when private mode is off. */}
          <ScratchpadPanel />
        </nav>

        {/* Right: Status & Actions */}
        <div className="flex items-center gap-4">
          {/* Queue Stats */}
          {stats && (
            <div className="flex items-center gap-3 text-xs">
              {stats.processing > 0 && (
                <span className="text-[var(--info)] flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-[var(--info)] animate-pulse-soft" />
                  {stats.processing} active
                </span>
              )}
              {stats.pending > 0 && (
                <span className="text-[var(--warning)]">
                  {stats.pending} queued
                </span>
              )}
              <span className="text-[var(--text-muted)]">
                {stats.completed} generated
              </span>
            </div>
          )}

          {/* Divider */}
          <div className="w-px h-4 bg-[var(--border-subtle)]" />

          {/* ComfyUI Status */}
          <div className="flex items-center gap-2">
            <div className={`w-1.5 h-1.5 rounded-full ${
              health?.comfyui_connected
                ? 'bg-[var(--success)]'
                : 'bg-[var(--text-muted)]'
            }`} />
            <span className="text-xs text-[var(--text-muted)] font-medium">
              ComfyUI
            </span>
          </div>

          {/* AI Models Status with Hover Popup */}
          <div
            className="relative"
            onMouseEnter={() => setShowAiPopup(true)}
            onMouseLeave={() => setShowAiPopup(false)}
          >
            <div className="flex items-center gap-2 cursor-pointer px-2 py-1 rounded hover:bg-[var(--bg-hover)] transition-colors">
              <div className={`w-1.5 h-1.5 rounded-full ${
                aiHealthLoading
                  ? 'bg-[var(--text-muted)] animate-pulse'
                  : aiConnected
                    ? 'bg-[var(--success)]'
                    : 'bg-[var(--error)]'
              }`} />
              <span className="text-xs text-[var(--text-muted)] font-medium">
                AI Models
              </span>
            </div>

            {/* Hover Popup */}
            {showAiPopup && (
              <div className="absolute right-0 top-full mt-2 w-72 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-xl shadow-xl p-4 z-50">
                <h3 className="text-sm font-semibold mb-3">AI Models Service</h3>

                {aiHealthLoading ? (
                  <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                    <div className="w-3 h-3 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
                    Connecting...
                  </div>
                ) : !aiConnected ? (
                  <div className="flex items-center gap-2 text-sm text-[var(--error)]">
                    <div className="w-2 h-2 rounded-full bg-[var(--error)]" />
                    Service unavailable
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-[var(--text-muted)] uppercase tracking-wider mb-0.5">Status</p>
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                          <span className="font-medium text-green-400">Connected</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-[var(--text-muted)] uppercase tracking-wider mb-0.5">Device</p>
                        <p className="font-medium">{aiHealth?.device || 'N/A'}</p>
                      </div>
                      {aiHealth?.gpu_available && (
                        <>
                          <div className="col-span-2">
                            <p className="text-[var(--text-muted)] uppercase tracking-wider mb-0.5">GPU</p>
                            <p className="font-medium truncate" title={aiHealth.gpu_name}>
                              {aiHealth.gpu_name || 'Available'}
                            </p>
                          </div>
                          {aiHealth.gpu_memory_total && (
                            <div>
                              <p className="text-[var(--text-muted)] uppercase tracking-wider mb-0.5">VRAM</p>
                              <p className="font-medium">
                                {aiHealth.gpu_memory_free ? (aiHealth.gpu_memory_free / 1024).toFixed(1) : '?'} / {(aiHealth.gpu_memory_total / 1024).toFixed(1)} GB
                              </p>
                            </div>
                          )}
                        </>
                      )}
                      {aiHealth?.queue && (
                        <div>
                          <p className="text-[var(--text-muted)] uppercase tracking-wider mb-0.5">Queue</p>
                          <p className="font-medium">{aiHealth.queue.pending} pending</p>
                        </div>
                      )}
                    </div>

                    {aiHealth?.loaded_models && aiHealth.loaded_models.length > 0 && (
                      <div className="pt-2 border-t border-[var(--border-subtle)]">
                        <p className="text-[var(--text-muted)] uppercase tracking-wider text-xs mb-1.5">Loaded Models</p>
                        <div className="flex flex-wrap gap-1">
                          {aiHealth.loaded_models.map((model) => (
                            <span
                              key={model}
                              className="px-1.5 py-0.5 bg-[var(--bg-primary)] rounded text-xs font-mono"
                            >
                              {model}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Private mode */}
          <PrivateModeToggle />

          {/* Refresh */}
          <button
            onClick={onRefresh}
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-subtle rounded-lg hover:bg-[var(--bg-hover)] cursor-pointer"
            title="Refresh"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
}
